import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { controlToastState, Modal, Select, TextInput } from "risa-oasis-ui_v2";

import { FirestoreService } from "../../../api/firebase/firestoreService";
import { FirestoreCollectionReference } from "../../../api/firebase/references";
import { ClientConfigModel } from "../../../data-model/clientConfig";
import { getStateLabelValueArray } from "../../../enums/usStates";
import { fetchClientFromFirebase } from "../../../redux/slice/clientConfiguration";
import { PlanDetails } from "../../../redux/slice/facilitySlice";
import { AppDispatch, RootState } from "../../../redux/store/store";

interface NewClientModalProps {
  data: ClientConfigModel | null;
  index?: number;
  onModalClose?: (boolean) => void;
  type: "add" | "edit";
  plans?: PlanDetails | null;
}

const NewClientModal = (props: NewClientModalProps) => {
  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );
  const [resetFormData, setResetFormData] = useState(false);
  const [loading, setLoading] = useState(false);

  const initialData: ClientConfigModel = {
    name: "",
    NPI: "",
    TaxID: "",
    PhoneNumber: "",
    FhirId: "",

    Address: {
      StreetAddress: "",
      City: "",
      State: "",
      ZIP: "",
    },
    emr_name: "",
    fhir_client: "",
    RisaFaxNumber: "",
    RisaPhoneNumber: "",
    created_at: "",
    updated_at: "",
    updated_by: "",
  };

  const [facilityData, setFacilityData] =
    useState<ClientConfigModel>(initialData);

  const dispatch = useDispatch<AppDispatch>();
  const emrOptions = useMemo(() => {
    return [
      {
        label: "Onco EMR",
        value: "oncoemr",
      },
    ];
  }, []);

  const areAllFieldsFilled = (): boolean => {
    const {
      name,
      NPI,
      TaxID,
      PhoneNumber,
      Address,
      FhirId,
      emr_name,
      fhir_client,
      RisaPhoneNumber,
      RisaFaxNumber,
    } = facilityData;

    if (
      !name ||
      !NPI ||
      !TaxID ||
      !PhoneNumber ||
      !FhirId ||
      !emr_name ||
      !fhir_client ||
      !RisaPhoneNumber ||
      !RisaFaxNumber
    ) {
      return false;
    }

    for (const field in Address) {
      if (!Address[field as keyof typeof Address]) {
        return false;
      }
    }

    return true;
  };

  const handleInputChangeForPrimaryDetails = (data) => {
    const { name, value } = data;
    setFacilityData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleChangeForAddressDetails = (data) => {
    const { name, value } = data;
    setFacilityData((prev) => ({
      ...prev,
      Address: {
        ...prev.Address,
        [name]: value,
      },
    }));
  };

  const handleOnClose = () => {
    props.onModalClose && props.onModalClose(false);
  };

  const addFacilityToDb = async () => {
    try {
      setLoading(true);

      const response = await FirestoreService.addDocument(
        FirestoreCollectionReference.client(),
        {
          ...facilityData,
          created_at: new Date().toISOString(),
        },
      );

      const newId = response["id"] ?? "";

      await FirestoreService.updateDocument(
        FirestoreCollectionReference.client(),
        newId,
        {
          DocId: newId,
        },
      );

      dispatch(fetchClientFromFirebase());
      controlToastState("facility-added-success");
      handleOnClose();
    } catch (error) {
      console.error("Error adding facility to firebase", error);
      controlToastState("facility-added-failure");
    } finally {
      setLoading(false);
    }
  };

  const updateFacilityInDb = async () => {
    setLoading(true);
    try {
      await FirestoreService.updateDocument(
        FirestoreCollectionReference.client(),
        props.data?.DocId || "",
        {
          ...props?.data,
          ...facilityData,
          updated_at: new Date().toISOString(),
          updated_by: user?.email ?? "",
        },
      );
    } catch (error) {
      console.error("Error updating facility to firebase", error);
    } finally {
      setLoading(false);
      dispatch(fetchClientFromFirebase());
      handleOnClose();
    }
  };

  useEffect(() => {
    if (resetFormData) {
      setResetFormData(false);
    }
  }, [facilityData]);

  useEffect(() => {
    if (props.data) {
      const newData: ClientConfigModel = {
        name: props?.data?.name ?? "",
        NPI: props?.data?.NPI ?? "",
        TaxID: props?.data?.TaxID ?? "",
        PhoneNumber: props?.data?.PhoneNumber ?? "",
        FhirId: props?.data?.FhirId ?? "",
        Address: {
          StreetAddress: props.data.Address?.StreetAddress,
          City: props?.data?.Address?.City ?? "",
          State: props?.data?.Address?.State ?? "",
          ZIP: props?.data?.Address?.ZIP ?? "",
        },
        emr_name: props?.data?.emr_name ?? "",
        fhir_client: props?.data?.fhir_client ?? "",
        RisaFaxNumber: props?.data?.RisaFaxNumber ?? "",
        RisaPhoneNumber: props?.data?.RisaPhoneNumber ?? "",
      };
      setFacilityData(newData);
    }
  }, [props.data]);

  return (
    <Modal
      dialogId={"new-client-modal"}
      onSave={props.type === "add" ? addFacilityToDb : updateFacilityInDb}
      title={props.type === "add" ? "Onboard New Facility" : "Edit Facility"}
      saveButtonText={loading ? "Saving..." : "Save"}
      cancelText={"Cancel"}
      heightPercentage={70}
      disableSave={!areAllFieldsFilled() || loading}
      onClose={handleOnClose}
    >
      <div className="facility-form-container">
        <form>
          <fieldset>
            <legend>Primary Details of Facility</legend>
            <TextInput
              id={"name"}
              label={"Client Name"}
              required
              onChange={handleInputChangeForPrimaryDetails}
              error={facilityData.name === "" ? "Please provide a client" : ""}
              resetField={resetFormData}
              defaultValue={facilityData.name}
            />

            <TextInput
              id={"NPI"}
              label={"NPI"}
              required
              onChange={handleInputChangeForPrimaryDetails}
              error={facilityData.NPI === "" ? "Please provide an NPI" : ""}
              resetField={resetFormData}
              defaultValue={facilityData.NPI}
            />
            <TextInput
              id={"TaxID"}
              label={"Tax ID"}
              required
              onChange={handleInputChangeForPrimaryDetails}
              error={facilityData.TaxID === "" ? "Please provide a Tax ID" : ""}
              resetField={resetFormData}
              defaultValue={facilityData.TaxID}
            />
            <TextInput
              id={"FhirId"}
              label={"FHIR ID"}
              required
              onChange={handleInputChangeForPrimaryDetails}
              error={
                facilityData?.FhirId === "" || facilityData?.FhirId == null
                  ? "Please provide a FHIR ID"
                  : ""
              }
              resetField={resetFormData}
              defaultValue={facilityData?.FhirId ?? ""}
            />
            <TextInput
              id={"PhoneNumber"}
              label={"Phone Number"}
              required
              onChange={handleInputChangeForPrimaryDetails}
              error={
                facilityData.PhoneNumber === ""
                  ? "Please provide a phone number"
                  : ""
              }
              resetField={resetFormData}
              defaultValue={facilityData.PhoneNumber}
            />
            <Select
              id={"emr_name"}
              label={"EMR Name"}
              options={emrOptions}
              onOptionChange={handleInputChangeForPrimaryDetails}
              placeholder={"Select an EMR ..."}
              required
              defaultValue={facilityData?.emr_name}
              error={
                facilityData?.emr_name === "" ? "Please select an EMR" : ""
              }
            />
            <TextInput
              id={"fhir_client"}
              label={"FHIR Client"}
              required
              onChange={handleInputChangeForPrimaryDetails}
              error={
                facilityData?.fhir_client === ""
                  ? "Please provide a FHIR client"
                  : ""
              }
              defaultValue={facilityData?.fhir_client}
            />
            <TextInput
              id={"RisaPhoneNumber"}
              label={"RISA Phone Number"}
              required
              onChange={handleInputChangeForPrimaryDetails}
              error={
                facilityData?.RisaPhoneNumber === ""
                  ? "Please provide a phone number"
                  : ""
              }
              defaultValue={facilityData?.RisaPhoneNumber}
            />
            <TextInput
              id={"RisaFaxNumber"}
              label={"RISA Fax Number"}
              required
              onChange={handleInputChangeForPrimaryDetails}
              error={
                facilityData?.RisaFaxNumber === ""
                  ? "Please provide a fax number"
                  : ""
              }
              defaultValue={facilityData?.RisaFaxNumber}
            />
          </fieldset>
          <fieldset>
            <legend>Address Details of Facility</legend>
            <TextInput
              id={"StreetAddress"}
              label={"Street Address"}
              required
              onChange={handleChangeForAddressDetails}
              error={
                facilityData?.Address?.StreetAddress === ""
                  ? "Please provide a street address"
                  : ""
              }
              resetField={resetFormData}
              defaultValue={facilityData?.Address?.StreetAddress}
            />
            <TextInput
              id={"City"}
              label={"City"}
              required
              onChange={handleChangeForAddressDetails}
              error={
                facilityData?.Address?.City === ""
                  ? "Please provide a city"
                  : ""
              }
              resetField={resetFormData}
              defaultValue={facilityData?.Address?.City}
            />
            <Select
              id={"State"}
              onOptionChange={handleChangeForAddressDetails}
              label={"State"}
              placeholder={"Select a state ..."}
              options={getStateLabelValueArray()}
              required
              error={
                facilityData?.Address?.State === ""
                  ? "Please select a state"
                  : ""
              }
              resetValue={resetFormData}
              defaultValue={facilityData?.Address?.State}
            />
            <TextInput
              id={"ZIP"}
              label={"Zip Code"}
              required
              onChange={handleChangeForAddressDetails}
              error={
                facilityData?.Address?.ZIP === ""
                  ? "Please provide a zip code"
                  : ""
              }
              resetField={resetFormData}
              defaultValue={facilityData?.Address?.ZIP}
            />
          </fieldset>
        </form>
      </div>
    </Modal>
  );
};

export default NewClientModal;
